import { Trash2, Edit } from "lucide-react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import DeleteMemberModal from "../members/DeleteMemberModal";
import UpdateMaintainerModal from "./UpdateMaintainerModal";
import {
  deleteMaintainer,
  updateMaintainer,
} from "../../../services/maintainerServices";
import Cookies from "js-cookie";
import { useQueryClient } from "@tanstack/react-query";

const AdminMaintainer = ({ permissionsData, users, refetch }) => {
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const role = Cookies.get("role");

  const confirmDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteMaintainer(selectedUser._id);
      toast.success("User deleted successfully");
      refetch();
      setShowDeleteModal(false);
    } catch (error) {
      toast.error(error.message || "Failed to delete user");
    }
  };

  const handleUpdate = async (userId, updatedData) => {
    setIsLoading(true);
    try {
      await updateMaintainer(userId, updatedData);
      toast.success("User updated successfully");
      refetch();
      queryClient.invalidateQueries({
        queryKey: ["maintainerData", userId],
      });

      setShowEditModal(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.message || "Failed to update user");
    } finally {
      setIsLoading(false);
    }
  };

  let canEdit = false;
  let canDelete = false;

  if (role === "admin") {
    canEdit = true;
    canDelete = true;
  } else if (role === "maintainer") {
    const modulePermissions = permissionsData?.permissions?.find(
      (p) => p.module === "Admin Portal Users"
    );
    canEdit = !!modulePermissions?.edit;
    canDelete = !!modulePermissions?.delete;
  }

  const showActions = canEdit || canDelete;

  return (
    <>
      <div className="bg-white rounded-t-2xl overflow-auto shadow-sm mt-6 min-h-[300px]">
        <table className="w-full text-sm border border-gray-300 table-auto">
          <thead className="bg-[#4F22D8] text-white text-sm">
            <tr>
              <th className="py-3 px-2 text-left font-bold border-r border-gray-400">
                No
              </th>
              <th className="py-2 px-2 text-left font-bold border-r border-gray-400">
                Email
              </th>
              <th className="py-2 px-2 text-left font-bold border-r border-gray-400">
                Name
              </th>
              <th className="py-2 px-2 text-left font-bold border-r border-gray-400">
                Role
              </th>
              {showActions && (
                <th className="py-2 px-2 text-left font-bold">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td
                  colSpan={showActions ? 5 : 4}
                  className="text-center py-6 text-gray-500"
                >
                  No users found.
                </td>
              </tr>
            ) : (
              users.map((user, idx) => (
                <tr
                  key={user._id}
                  className="border-t border-gray-300 hover:bg-gray-50"
                >
                  <td className="px-2 py-2 border-r border-gray-300">
                    {idx + 1}
                  </td>
                  <td className="px-2 py-2 border-r border-gray-300">
                    {user.email}
                  </td>
                  <td className="px-2 py-2 border-r border-gray-300">
                    {user.username || user.fullName || "-"}
                  </td>
                  <td className="px-2 py-2 border-r border-gray-300 capitalize">
                    {user.role}
                  </td>

                  {showActions && (
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2">
                        {canEdit && (
                          <button
                            onClick={() => openEditModal(user)}
                            className="text-[#4F22D8] font-semibold text-xs flex items-center gap-1 hover:text-[#3d1ba6] transition-colors"
                          >
                            <Edit size={14} /> Edit
                          </button>
                        )}

                        {canEdit && canDelete && (
                          <div className="w-px h-4 bg-gray-300" />
                        )}

                        {canDelete && (
                          <button
                            onClick={() => confirmDelete(user)}
                            className="text-[#D40404] font-semibold text-xs flex items-center gap-1 hover:text-red-700 transition-colors"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {selectedUser && (
        <UpdateMaintainerModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onUpdate={handleUpdate}
          user={selectedUser}
          isLoading={isLoading}
        />
      )}

      {/* Delete Modal */}
      {selectedUser && (
        <DeleteMemberModal
          isOpen={showDeleteModal}
          onRequestClose={() => setShowDeleteModal(false)}
          onDelete={handleDelete}
          itemName={selectedUser.email}
          itemType="maintainer"
        />
      )}
    </>
  );
};

export default AdminMaintainer;
